import React, { createContext, useState, useContext, useRef } from "react";
import { Snackbar } from "@material/react-snackbar";
import "./SnackBar.scss";

type ContextType = React.Dispatch<Alert>;

type AlertAction = {
  label: string;
  action: React.DispatchWithoutAction;
};
export type Alert = {
  key: string;
  message: string;
  isSuccess: boolean;
  action?: AlertAction;
  timeout?: number;
  /**
   * Keeps the alert until its action is taken: it is shown again whenever it
   * times out or another alert that replaced it closes.
   */
  sticky?: boolean;
};

// The snackbar cannot stay open indefinitely; this is the longest timeout it accepts.
const maxTimeoutMs = 10000;
const defaultTimeoutMs = 4000;

export const SnackBarContext = createContext<ContextType>(() => undefined);

export const SnackBarProvider: React.FC = ({ children }) => {
  // `showing` increments on every show, so the Snackbar remounts and reopens
  // even when the same alert is shown again.
  const [shown, setShown] = useState<{ alert: Alert; showing: number }>();
  const stickyAlert = useRef<Alert>();

  const show = (alert: Alert) =>
    setShown((prev) => ({ alert, showing: (prev?.showing ?? 0) + 1 }));

  const addAlert = (alert: Alert) => {
    if (alert.sticky) {
      stickyAlert.current = alert;
    }
    show(alert);
  };

  const onClose = (reason) => {
    const closed = shown?.alert;

    // the "action" string here is hard-coded into the snackbar component
    if (reason === "action") {
      if (closed === stickyAlert.current) {
        stickyAlert.current = undefined;
      }

      closed?.action?.action();
    }

    if (stickyAlert.current) {
      show(stickyAlert.current);
    }
  };

  const alert = shown?.alert;

  return (
    <SnackBarContext.Provider value={addAlert}>
      {children}
      <Snackbar
        key={shown && `${shown.alert.key}-${shown.showing}`}
        actionText={alert?.action?.label ?? "dismiss"}
        className={alert?.isSuccess ? "success" : "error"}
        open={!!alert}
        aria-describedby="client-snackbar"
        message={alert?.message}
        onClose={onClose}
        timeoutMs={
          alert?.timeout ?? (alert?.sticky ? maxTimeoutMs : defaultTimeoutMs)
        }
      />
    </SnackBarContext.Provider>
  );
};

export function useSnackBar(): React.Dispatch<Alert> {
  return useContext(SnackBarContext);
}

export const MockSnackBarProvider: React.FC<{
  addAlert?: (alert: Alert) => void;
}> = ({ addAlert, children }) => (
  <SnackBarContext.Provider value={addAlert ?? (() => undefined)}>
    {children}
  </SnackBarContext.Provider>
);
